import React, { useEffect, useMemo, useState } from 'react';
import { FiArrowLeft, FiCalendar, FiCheckCircle, FiChevronLeft, FiChevronRight, FiClock } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { isParentAccount } from '../mymod/accountRole';
import fnc from '../mymod/commFunction';
import { fetchReportChildren } from '../page_lmsReport/reportApi';
import { fetchGuardianScheduleWeek } from './services/guardianSchedulerApi';
import { addDays, formatKoreanDate, formatMinutes, getTodayKey, getWeekDays } from './utils/dateUtils';
import './GuardianScheduler.scss';

const statusLabels = { planned: '미완료', progress: '진행 중', completed: '완료' };
const dateKey = (value) => String(value || '').slice(0, 10);

export default function GuardianScheduler() {
  const navigate = useNavigate();
  const isParent = isParentAccount();
  const sessionToken = fnc.getCookie('appSessionToken');
  const [children, setChildren] = useState([]);
  const [studentId, setStudentId] = useState('');
  const [selectedDate, setSelectedDate] = useState(getTodayKey);
  const [childrenLoading, setChildrenLoading] = useState(true);
  const [childrenError, setChildrenError] = useState('');
  const [scheduleError, setScheduleError] = useState('');
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [weekData, setWeekData] = useState(null);
  const [childrenRetry, setChildrenRetry] = useState(0);
  const [scheduleRetry, setScheduleRetry] = useState(0);
  const week = useMemo(() => getWeekDays(selectedDate), [selectedDate]);
  const startDate = week[0].key;
  const endDate = week[6].key;
  const selectionKey = JSON.stringify([sessionToken, studentId, startDate, endDate]);
  const currentData = weekData?.key === selectionKey ? weekData : null;
  const plans = (currentData?.plans || []).filter((plan) => dateKey(plan.studyDate) === selectedDate);
  const submitted = (currentData?.reports || []).some((report) => dateKey(report.studyDate) === selectedDate && report.submittedAt);
  const selectedChild = children.find((child) => String(child.studentId) === studentId);
  const error = childrenError || scheduleError;
  const completed = plans.filter((plan) => plan.status === 'completed').length;
  const plannedMinutes = plans.reduce((total, plan) => total + (Number(plan.expectedMinutes) || 0), 0);
  const actualMinutes = plans.reduce((total, plan) => total + (Number(plan.actualMinutes) || 0), 0);

  useEffect(() => {
    let active = true;
    setChildren([]);
    setStudentId('');
    setWeekData(null);
    setChildrenError('');
    setChildrenLoading(isParent);
    if (!isParent) return undefined;
    fetchReportChildren().then((items) => {
      if (!active) return;
      setChildren(items);
      setStudentId(String(items[0]?.studentId || ''));
    }).catch((requestError) => {
      if (active) setChildrenError(requestError.message);
    }).finally(() => {
      if (active) setChildrenLoading(false);
    });
    return () => { active = false; };
  }, [isParent, sessionToken, childrenRetry]);

  useEffect(() => {
    let active = true;
    setWeekData(null);
    setScheduleError('');
    if (!isParent || !studentId || childrenLoading) {
      setScheduleLoading(false);
      return undefined;
    }
    setScheduleLoading(true);
    fetchGuardianScheduleWeek({ studentId, startDate, endDate }).then((data) => {
      if (active) setWeekData({ ...data, key: selectionKey });
    }).catch((requestError) => {
      if (active) setScheduleError(requestError.message);
    }).finally(() => {
      if (active) setScheduleLoading(false);
    });
    return () => { active = false; };
  }, [isParent, studentId, startDate, endDate, selectionKey, childrenLoading, scheduleRetry]);

  const chooseChild = (value) => {
    setWeekData(null);
    setScheduleError('');
    setStudentId(value);
  };

  return (
    <main className="guardian-scheduler" aria-label="자녀 학습 스케줄">
      <header className="guardian-scheduler__topbar">
        <div>
          <button type="button" onClick={() => navigate('/menu')} aria-label="메뉴로 돌아가기"><FiArrowLeft aria-hidden="true" /></button>
          <FiCalendar aria-hidden="true" /><strong>자녀 학습 스케줄</strong>
        </div>
      </header>
      <div className="guardian-scheduler__scroll">
        <div className="guardian-scheduler__container">
          <header className="guardian-scheduler__intro">
            <h1>계획부터 실천까지, 한눈에</h1>
            <p>자녀가 기록한 학습 계획과 완료 상태를 확인하세요.</p>
          </header>
          {!isParent && <div className="guardian-scheduler__message">학부모 계정으로 로그인해 주세요.</div>}
          {childrenLoading && <div className="guardian-scheduler__message" role="status">연결된 자녀를 불러오는 중입니다.</div>}
          {isParent && !childrenLoading && !children.length && !childrenError && (
            <div className="guardian-scheduler__message">연결된 자녀가 없습니다. 학원에 학부모 휴대전화 등록 정보를 확인해 주세요.</div>
          )}
          {!!children.length && !childrenLoading && (
            <>
              <section className="guardian-scheduler__child" aria-label="조회할 자녀 선택">
                <div><label htmlFor="guardian-scheduler-child">자녀 선택</label><span>학습 기록 조회</span></div>
                <select id="guardian-scheduler-child" value={studentId} onChange={(event) => chooseChild(event.target.value)}>
                  {children.map((child) => <option key={child.studentId} value={child.studentId}>
                    {child.name || '학생'}{children.some((other) => other.studentId !== child.studentId && other.name === child.name) ? ` (${child.stCode})` : ''}
                  </option>)}
                </select>
              </section>
              <section className="guardian-scheduler__calendar" aria-label="학습 날짜 선택">
                <div className="guardian-scheduler__week-nav">
                  <button type="button" onClick={() => setSelectedDate(addDays(selectedDate, -7))} aria-label="이전 주"><FiChevronLeft aria-hidden="true" /></button>
                  <strong>{formatKoreanDate(selectedDate)}</strong>
                  <button type="button" onClick={() => setSelectedDate(addDays(selectedDate, 7))} aria-label="다음 주"><FiChevronRight aria-hidden="true" /></button>
                </div>
                <div className="guardian-scheduler__week">
                  {week.map((day) => <button type="button" key={day.key} onClick={() => setSelectedDate(day.key)}
                    aria-label={formatKoreanDate(day.key)} aria-pressed={selectedDate === day.key}>
                    <span>{day.weekday}</span><strong>{day.day}</strong>
                  </button>)}
                </div>
                <button type="button" className="guardian-scheduler__today" onClick={() => setSelectedDate(getTodayKey())}>오늘</button>
              </section>
            </>
          )}
          {error && <div className="guardian-scheduler__message guardian-scheduler__message--error" role="alert">{error}
            <button type="button" onClick={() => (childrenError ? setChildrenRetry : setScheduleRetry)((value) => value + 1)}>다시 확인</button>
          </div>}
          {scheduleLoading && !childrenLoading && <div className="guardian-scheduler__message" role="status">학습 스케줄을 불러오는 중입니다.</div>}
          {currentData && !scheduleLoading && !childrenLoading && !error && (
            <>
              <section className="guardian-scheduler__summary" aria-label="선택한 날짜의 학습 요약">
                <article><FiClock aria-hidden="true" /><span>계획 시간</span><strong>{formatMinutes(plannedMinutes)}</strong></article>
                <article><FiClock aria-hidden="true" /><span>실제 시간</span><strong>{formatMinutes(actualMinutes)}</strong></article>
                <article><FiCheckCircle aria-hidden="true" /><span>완료한 계획</span><strong>{completed} / {plans.length}</strong></article>
              </section>
              <section className="guardian-scheduler__plans" aria-label="자녀의 학습 계획">
                <div className="guardian-scheduler__section-title"><h2>{selectedChild?.name || '자녀'}의 학습 계획</h2>
                  {submitted && <span>하루 학습 마감</span>}
                </div>
                {!plans.length && <div className="guardian-scheduler__message">이 날짜에 등록된 학습 계획이 없습니다.</div>}
                {plans.map((plan) => <article className="guardian-scheduler__plan" key={plan.sno}>
                  <header><span>{plan.startTime} – {plan.endTime}</span><span className={`guardian-scheduler__status is-${plan.status}`}>{statusLabels[plan.status] || '미완료'}</span></header>
                  <h3>{plan.subject || '학습'}</h3>
                  <p>{plan.planContent || '학습 내용이 없습니다.'}</p>
                  <dl><div><dt>계획</dt><dd>{formatMinutes(plan.expectedMinutes)}</dd></div><div><dt>실제</dt><dd>{formatMinutes(plan.actualMinutes)}</dd></div>
                    {Number(plan.understanding) > 0 && <div><dt>이해도</dt><dd>{Math.min(5, Number(plan.understanding))} / 5</dd></div>}
                  </dl>
                  {plan.actualContent && <div className="guardian-scheduler__record"><strong>실제 학습 내용</strong><p>{plan.actualContent}</p></div>}
                  {plan.memo && <div className="guardian-scheduler__record"><strong>학습 메모</strong><p>{plan.memo}</p></div>}
                </article>)}
              </section>
              <p className="guardian-scheduler__footnote">자녀가 직접 기록한 내용입니다. 계획 작성과 완료 기록은 학생 계정에서 할 수 있습니다.</p>
            </>
          )}
        </div>
      </div>
    </main>
  );
}
